import type {
  DesignDocument,
  DesignObject,
  MmRect,
  ProductConfig,
  ProductViewId,
} from '../domain/types';
import { isVisibleArtworkObject } from '../domain/design';
import { resolvePrintableArea } from '../products';
import { objectBounds, unionRects } from '../validation/geometry';
import { inspectA4SheetFit } from './a4-sheet';

export interface TransferGroupPlan {
  id: string;
  viewId: ProductViewId;
  label: string;
  objectIds: string[];
  artworkBoundsMm: MmRect;
  placementAreaMm: { width: number; height: number };
  maxTransferSizeMm: { width: number; height: number };
}

export interface CroppedTransferSource {
  /** Render-only clone. Objects retain their physical size and relative placement. */
  design: DesignDocument;
  artworkBoundsMm: MmRect;
}

export interface A4TransferObjectGroup {
  objectIds: string[];
  artworkBoundsMm: MmRect;
}

interface IndexedObject {
  object: DesignObject;
  index: number;
}

interface WorkingTransferGroup {
  objects: IndexedObject[];
  artworkBoundsMm: MmRect;
}

export function visibleSurfaceObjects(design: DesignDocument, viewId: ProductViewId) {
  return design.surfaces[viewId]?.objects.filter(isVisibleArtworkObject) ?? [];
}

function rectArea(rect: MmRect): number {
  return rect.width * rect.height;
}

function rectGapSquared(left: MmRect, right: MmRect): number {
  const horizontalGap = Math.max(
    0,
    left.x - (right.x + right.width),
    right.x - (left.x + left.width),
  );
  const verticalGap = Math.max(
    0,
    left.y - (right.y + right.height),
    right.y - (left.y + left.height),
  );
  return horizontalGap * horizontalGap + verticalGap * verticalGap;
}

function compareWorkingGroups(left: WorkingTransferGroup, right: WorkingTransferGroup): number {
  return (
    left.artworkBoundsMm.y - right.artworkBoundsMm.y ||
    left.artworkBoundsMm.x - right.artworkBoundsMm.x ||
    left.objects[0]!.index - right.objects[0]!.index
  );
}

/**
 * Partitions one garment side into the smallest practical set of A4 transfers.
 * Nearby objects are merged while their complete 1:1 bounding box still fits
 * one A4 sheet. Objects that would force that box beyond A4 remain in separate
 * transfers and keep their original garment coordinates for placement.
 */
export function partitionObjectsIntoA4Transfers(
  objects: readonly DesignObject[],
  pagePaddingMm: number,
): A4TransferObjectGroup[] {
  const groups: WorkingTransferGroup[] = objects.map((object, index) => {
    const boundsMm = objectBounds(object);
    return {
      objects: [{ object, index }],
      artworkBoundsMm: boundsMm,
    };
  });

  while (groups.length > 1) {
    let best:
      | {
          leftIndex: number;
          rightIndex: number;
          mergedBoundsMm: MmRect;
          gapSquared: number;
          areaGrowth: number;
          firstObjectIndex: number;
        }
      | undefined;

    for (let leftIndex = 0; leftIndex < groups.length - 1; leftIndex += 1) {
      const left = groups[leftIndex]!;
      for (let rightIndex = leftIndex + 1; rightIndex < groups.length; rightIndex += 1) {
        const right = groups[rightIndex]!;
        const mergedBoundsMm = unionRects([left.artworkBoundsMm, right.artworkBoundsMm])!;
        if (!inspectA4SheetFit(mergedBoundsMm, pagePaddingMm).fits) continue;

        const candidate = {
          leftIndex,
          rightIndex,
          mergedBoundsMm,
          gapSquared: rectGapSquared(left.artworkBoundsMm, right.artworkBoundsMm),
          areaGrowth:
            rectArea(mergedBoundsMm) -
            rectArea(left.artworkBoundsMm) -
            rectArea(right.artworkBoundsMm),
          firstObjectIndex: Math.min(left.objects[0]!.index, right.objects[0]!.index),
        };
        if (
          !best ||
          candidate.gapSquared < best.gapSquared ||
          (candidate.gapSquared === best.gapSquared && candidate.areaGrowth < best.areaGrowth) ||
          (candidate.gapSquared === best.gapSquared &&
            candidate.areaGrowth === best.areaGrowth &&
            candidate.firstObjectIndex < best.firstObjectIndex)
        ) {
          best = candidate;
        }
      }
    }

    if (!best) break;
    const left = groups[best.leftIndex]!;
    const right = groups[best.rightIndex]!;
    const merged: WorkingTransferGroup = {
      objects: [...left.objects, ...right.objects].sort((a, b) => a.index - b.index),
      artworkBoundsMm: best.mergedBoundsMm,
    };
    groups.splice(best.rightIndex, 1);
    groups.splice(best.leftIndex, 1, merged);
  }

  return groups.sort(compareWorkingGroups).map(group => ({
    objectIds: group.objects.map(({ object }) => object.id),
    artworkBoundsMm: group.artworkBoundsMm,
  }));
}

/** Front and back stay separate; either side may need more than one A4 transfer. */
export function deriveTransferGroups(
  design: DesignDocument,
  product: ProductConfig,
): TransferGroupPlan[] {
  return product.printableAreas.flatMap(baseArea => {
    const objects = visibleSurfaceObjects(design, baseArea.viewId);
    const artworkBoundsMm = unionRects(objects.map(objectBounds));
    if (!artworkBoundsMm) return [];
    const area = resolvePrintableArea(product, design.variantId, design.size, baseArea.viewId);
    const workflow = product.productionRules.workflow;
    const objectGroups =
      workflow?.kind === 'print-cut-transfer'
        ? partitionObjectsIntoA4Transfers(objects, workflow.pagePaddingMm)
        : [{ objectIds: objects.map(object => object.id), artworkBoundsMm }];

    return objectGroups.map((group, index) => ({
      // Keep the production filename stable when another transfer is added later.
      // A side with one motif is therefore still `front-transfer-1`.
      id: `${baseArea.viewId}-transfer-${index + 1}`,
      viewId: baseArea.viewId,
      label:
        objectGroups.length === 1
          ? area.label
          : `${area.label} · motív ${index + 1}/${objectGroups.length}`,
      objectIds: group.objectIds,
      artworkBoundsMm: group.artworkBoundsMm,
      placementAreaMm: structuredClone(area.sizeMm),
      maxTransferSizeMm: structuredClone(area.maxPrintSizeMm),
    }));
  });
}

/** Render-only clone containing exactly the objects assigned to one transfer. */
export function createTransferRenderSource(
  design: DesignDocument,
  viewId: ProductViewId,
  objectIds: readonly string[],
): DesignDocument {
  const sourceSurface = design.surfaces[viewId];
  if (!sourceSurface) throw new Error(`Missing design surface ${viewId}`);
  const ids = new Set(objectIds);
  const transferDesign = structuredClone(design);
  const transferSurface = transferDesign.surfaces[viewId]!;
  transferSurface.objects = transferSurface.objects.filter(object => ids.has(object.id));
  if (transferSurface.objects.length !== ids.size) {
    throw new Error(`Transfer for ${viewId} references a missing design object`);
  }
  return transferDesign;
}

/**
 * Layout-only clone retained for consumers that need local object coordinates.
 * Production raster cropping must use `renderPrintSurface({ cropMm })` instead,
 * so procedural effects remain anchored to the original placement surface.
 */
export function createCroppedTransferSource(
  design: DesignDocument,
  viewId: ProductViewId,
): CroppedTransferSource {
  const surface = design.surfaces[viewId];
  if (!surface) throw new Error(`Missing design surface ${viewId}`);
  const visibleObjects = visibleSurfaceObjects(design, viewId);
  const artworkBoundsMm = unionRects(visibleObjects.map(objectBounds));
  if (!artworkBoundsMm) throw new Error(`Surface ${viewId} contains no visible artwork`);

  const cropped = structuredClone(design);
  const croppedSurface = cropped.surfaces[viewId]!;
  croppedSurface.printAreaMm = {
    width: artworkBoundsMm.width,
    height: artworkBoundsMm.height,
  };
  croppedSurface.objects = visibleObjects.map(object => ({
    ...structuredClone(object),
    transform: {
      ...structuredClone(object.transform),
      positionMm: {
        x: object.transform.positionMm.x - artworkBoundsMm.x,
        y: object.transform.positionMm.y - artworkBoundsMm.y,
      },
    },
  }));
  return { design: cropped, artworkBoundsMm };
}
